import clsx from 'clsx';
import type { ReactNode } from 'react';

import { SETTINGS_ACTION_BUTTON, SETTINGS_ACTION_SLOT } from '@/auth/components/shared';
import {
  type SocialIdentity,
  type SocialProvider,
  socialProviderName,
} from '@/auth/utils/socialSignIn';
import Button from '@/componentsv2/Button';
import Tooltip from '@/componentsv2/Tooltip';

const LAST_WAY_IN = 'Set a password before disconnecting your only way to sign in.';

interface ConnectedAccountListProps {
  /** The providers offered, in order. */
  providers: SocialProvider[];
  identities: SocialIdentity[];
  hasPassword: boolean;
  busy?: boolean;
  /** The provider's own sign-in button, which connects it. */
  connectButton: (provider: SocialProvider) => ReactNode;
  onDisconnect: (provider: SocialProvider) => void;
}

/** One row per offered provider: its connected email and Disconnect, or its sign-in button. */
export default function ConnectedAccountList({
  providers,
  identities,
  hasPassword,
  busy = false,
  connectButton,
  onDisconnect,
}: ConnectedAccountListProps): JSX.Element {
  // Without a password, the only connected provider is the only way in.
  const lastWayIn = !hasPassword && identities.length === 1;

  return (
    <ul className="flex flex-col divide-y divide-surface-border">
      {providers.map((provider) => {
        const name = socialProviderName(provider);
        const identity = identities.find((i) => i.provider === provider);
        return (
          <li key={provider} className="flex flex-wrap items-center justify-between gap-16 py-12">
            <div className="flex min-w-0 flex-col">
              <span className="font-bold text-on-surface">{name}</span>
              <span className="break-all text-14 text-on-surface-secondary">
                {identity ? identity.email : 'Not connected'}
              </span>
            </div>
            {identity ? (
              <Tooltip title={lastWayIn ? LAST_WAY_IN : ''}>
                <span className={clsx('block', SETTINGS_ACTION_SLOT)}>
                  <Button
                    className={SETTINGS_ACTION_BUTTON}
                    variant="secondary"
                    size="medium"
                    disabled={busy || lastWayIn}
                    onClick={() => onDisconnect(provider)}
                    aria-label={`Disconnect ${name}`}
                    aria-describedby={lastWayIn ? `${provider}-last-way-in` : undefined}
                  >
                    Disconnect
                  </Button>
                  {lastWayIn && (
                    <span id={`${provider}-last-way-in`} className="sr-only">
                      {LAST_WAY_IN}
                    </span>
                  )}
                </span>
              </Tooltip>
            ) : (
              // Google's button sizes itself to this slot.
              <div className={SETTINGS_ACTION_SLOT}>{connectButton(provider)}</div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
